import { Heart, Car, Fuel, Gauge, Settings2, Calendar, ArrowRight } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { getLayout5ThemeStyle } from './theme';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { useStore } from '@/contexts/StoreContext';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import { getBrandLogo } from '@/lib/brandLogos';
import './styles.css';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  manufactureYear: number;
  modelYear: number;
  color: string;
  transmission: string;
  isZeroKm: boolean;
  store?: string;
  type?: number;
  changed?: {
    featured?: boolean;
  };
}

interface VehicleCardProps {
  vehicle: Vehicle;
  whatsappUrl?: string;
}

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const shortenTransmission = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return '—';
  return normalized
    .replace(/autom[aá]tico/i, 'Aut.')
    .replace(/semi-?autom[aá]tico/i, 'S-aut.')
    .replace(/manual/i, 'Man.')
    .replace(/cvt/i, 'CVT');
};

export const VehicleCard = ({ vehicle }: VehicleCardProps) => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const themeStyle = getLayout5ThemeStyle(colors);
  const { availableStores, selectedStore } = useStore();
  const [isFavorited, setIsFavorited] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [vehicleSlug, setVehicleSlug] = useState<string>(() => {
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);

  const secondaryColor = colors?.secondary || '#64748b';
  const buttonColor = colors?.button || '#3b82f6';
  const titleColor = colors?.title || '#1e293b';
  const textColor = colors?.text || '#64748b';

  const storeId = vehicle.store;
  const vehicleStore = useMemo(() => {
    if (!config?.stores) return null;
    return storeId
      ? config.stores.find((s: any) => s._id === storeId)
      : (selectedStore || config.stores[0]);
  }, [config?.stores, storeId, selectedStore]);
  const showStoreBadge = availableStores.length > 1 && vehicleStore && storeId;
  const isFeatured = !!vehicle.changed?.featured;

  useEffect(() => {
    const init = async () => {
      try {
        secureGetItem('favorites').then(favorites => {
          if (favorites && Array.isArray(favorites)) {
            setIsFavorited(favorites.includes(vehicle.id));
          }
        }).catch(() => {});

        generateVehicleSlug(vehicle).then(slug => {
          setVehicleSlug(slug);
        }).catch(() => {});
      } catch {
        /* card still renders */
      }
    };
    init();
    setBrandLogoFailed(false);
  }, [vehicle.id, vehicle.brand, vehicle.type]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const favorites = await secureGetItem('favorites') || [];
    let updatedFavorites = Array.isArray(favorites) ? [...favorites] : [];

    if (updatedFavorites.includes(vehicle.id)) {
      updatedFavorites = updatedFavorites.filter((id: number) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      updatedFavorites.push(vehicle.id);
      setIsFavorited(true);
    }

    await secureSetItem('favorites', updatedFavorites);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price);

  const formatMileage = (mileage: number) => {
    if (mileage === 0) return 'Zero KM';
    if (mileage >= 1000) {
      return `${new Intl.NumberFormat('pt-BR').format(mileage)} km`;
    }
    return `${mileage} km`;
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoaded(true);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isImageLoading) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const brandLogo = useMemo(
    () => getBrandLogo(vehicle.brand, vehicle.type),
    [vehicle.brand, vehicle.type]
  );
  const imageUrl = vehicle.photos?.[0];
  const optimized = imageUrl ? generateResponsiveImageUrls(imageUrl, 340, 212) : null;
  const loadingStrategy = getImageLoadingStrategy(true, true);

  const specs = [
    {
      key: 'ano',
      label: 'Ano',
      value: vehicle.modelYear ? String(vehicle.modelYear) : '—',
      icon: Calendar,
    },
    {
      key: 'cambio',
      label: 'Câmbio',
      value: shortenTransmission(vehicle.transmission || '—'),
      icon: Settings2,
    },
    {
      key: 'km',
      label: 'Km',
      value: vehicle.mileage !== undefined && vehicle.mileage !== null
        ? formatMileage(vehicle.mileage)
        : '—',
      icon: Gauge,
    },
    {
      key: 'combustivel',
      label: 'Comb.',
      value: vehicle.fuel || '—',
      icon: Fuel,
    },
  ];

  return (
    <article className="wl5-vehicle-card group h-full" style={themeStyle}>
      <Link
        to={`/estoque/${vehicleSlug}`}
        className="wl5-vehicle-card-link flex flex-col h-full"
        onClick={handleCardClick}
      >
        <div className="wl5-vehicle-media">
          {isImageLoading && !imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--wl5-bg)] z-20">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--wl5-border)] border-t-[var(--wl5-button)]" />
            </div>
          )}

          {isFeatured && (
            <span className="wl5-vehicle-list-ribbon" style={{ backgroundColor: buttonColor }}>
              Destaque
            </span>
          )}

          <button
            type="button"
            onClick={toggleFavorite}
            className="wl5-grid-favorite"
            style={{
              borderColor: isFavorited ? buttonColor : 'rgb(255 255 255 / 0.9)',
              color: isFavorited ? buttonColor : textColor,
            }}
            title={isFavorited ? 'Remover dos favoritos' : 'Favoritar'}
            aria-label={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <Heart className="h-4 w-4" fill={isFavorited ? buttonColor : 'none'} />
          </button>

          {optimized ? (
            <img
              src={optimized.src}
              srcSet={optimized.srcSet}
              sizes={optimized.sizes || '(max-width: 640px) 90vw, 320px'}
              alt={`${vehicle.brand} ${vehicle.model}`}
              className="wl5-vehicle-list-img"
              loading={loadingStrategy.loading}
              fetchpriority={loadingStrategy.fetchpriority}
              decoding="async"
              width={optimized.width}
              height={optimized.height}
              onLoad={handleImageLoad}
              onError={(e) => { e.currentTarget.style.display = 'none'; setIsImageLoading(false); setImageLoaded(true); }}
            />
          ) : (
            <div className="wl5-vehicle-list-img wl5-vehicle-list-img--empty">
              <Car className="h-10 w-10 opacity-40" />
            </div>
          )}

          <span className="wl5-grid-price-badge" style={{ backgroundColor: buttonColor }}>
            {formatPrice(vehicle.price)}
          </span>

          <div className="wl5-vehicle-list-media-tools">
            {vehicle.isZeroKm && (
              <span className="wl5-vehicle-list-media-pill">Zero KM</span>
            )}
            {showStoreBadge && (
              <span
                className="wl5-vehicle-list-media-pill wl5-vehicle-list-media-pill--store"
                style={{ backgroundColor: secondaryColor }}
              >
                {vehicleStore?.company}
              </span>
            )}
          </div>
        </div>

        <div className="wl5-vehicle-body">
          <div className="wl5-grid-head">
            <div className="wl5-grid-head-text">
              <h3 className="wl5-grid-title" style={{ color: titleColor }}>
                {formatLabel(vehicle.brand)} {formatLabel(vehicle.model)}
              </h3>
              {vehicle.version && (
                <p className="wl5-grid-version">{formatLabel(vehicle.version)}</p>
              )}
            </div>

            <span
              className="wl5-grid-brand"
              title={formatLabel(vehicle.brand)}
              aria-label={formatLabel(vehicle.brand)}
            >
              {brandLogo && !brandLogoFailed ? (
                <img
                  src={brandLogo}
                  alt=""
                  className="wl5-grid-brand-img"
                  onError={() => setBrandLogoFailed(true)}
                />
              ) : (
                <span className="wl5-grid-brand-fallback" style={{ color: buttonColor }}>
                  {(vehicle.brand || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </span>
          </div>

          <div className="wl5-grid-specs">
            {specs.map(({ key, label, value, icon: Icon }) => (
              <div key={key} className="wl5-grid-spec">
                <Icon className="wl5-grid-spec-icon" style={{ color: textColor }} />
                <div className="wl5-grid-spec-copy">
                  <span className="wl5-grid-spec-label">{label}</span>
                  <span className="wl5-grid-spec-value" style={{ color: titleColor }}>
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <span className="wl5-grid-cta" style={{ backgroundColor: buttonColor }}>
            Ver detalhes
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </Link>
    </article>
  );
};
